// components/dashboard/AIEmailShowcase.jsx
// Area 2 — "Your customer emails": an interactive preview tool that builds trust.
// The merchant picks a REAL order + a message type and sees exactly what their
// customer would receive.
//
// Honesty / cost:
//  - Orders come from the merchant's own store (GET /api/orders — same source as the
//    Orders screen), sorted newest-first client-side. No new PII surface.
//  - Types come from /api/templates only — the real templates that exist for this
//    shop. Nothing invented.
//  - The preview is the REAL output of /api/messages/preview (OpenAI gpt-4o-mini),
//    generated on demand (button click only — never auto on load, saving an OpenAI
//    call + latency every dashboard load). It's quota-free (dryRun) — nothing is
//    sent.
//  - The typewriter only reveals text that already came back; the copy isn't scripted.
//  - No orders yet → a clearly-labelled sample, never a fabricated real order.

import React, { useState, useRef, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Sparkles, Mail, RefreshCw, ChevronDown } from "lucide-react";
import { API_BASE_URL } from "@/constants";
import { authenticatedFetch } from "@/utils/authenticatedFetch";

const TYPE_LABELS = {
  thank_you: "Thank you",
  review_request: "Review request",
  vip_upsell: "VIP upsell",
  custom_offer: "Custom offer",
  win_back: "Win back",
};
const prettyType = (t) => TYPE_LABELS[t] || String(t || "email").replace(/_/g, " ");

const SAMPLE_ORDER = { __sample: true };

// Sentinel for "shop has no templates yet" — the preview then runs on the
// backend's built-in PREVIEW-ONLY sample template (messagePreview.js
// SAMPLE_TEMPLATES). Real AI, quota-free, clearly labelled; real sends are
// untouched — a new merchant sees the magic before creating anything.
const SAMPLE_TEMPLATE_ID = '__sample_template__';

const prefersReduced = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function firstName(order) {
  const n = order?.customer?.first_name || order?.customer?.name || "there";
  return String(n).trim().split(" ")[0] || "there";
}
function firstProduct(order) {
  return order?.line_items?.[0]?.title || order?.product_names || "your order";
}
function orderLabel(order) {
  if (order?.__sample) return "Sample order · Sarah · Blue Ceramic Mug";
  const num = order?.order_number || order?.order_id || "";
  return `${num ? num + " · " : ""}${firstName(order)} · ${firstProduct(order)}`;
}
function orderToPreview(order) {
  if (order?.__sample) {
    return {
      customer_name: "Sarah",
      product_name: "Blue Ceramic Mug",
      order_amount: 42.0,
      order_number: "SAMPLE-1001",
    };
  }
  return {
    customer_name: firstName(order),
    product_name: firstProduct(order),
    order_amount: Number(order?.total_price) || 0,
    order_number: order?.order_number || order?.order_id || "—",
  };
}

export default function AIEmailShowcase({ shopDomain, onTemplatesLoaded }) {
  const [orders, setOrders] = useState([]);
  const [isSample, setIsSample] = useState(false);
  const [types, setTypes] = useState([]); // [{ type, template_id, label }]
  const [orderIdx, setOrderIdx] = useState(0);
  const [templateId, setTemplateId] = useState("");

  const [phase, setPhase] = useState("idle"); // idle | loading | revealing | done | error
  const [email, setEmail] = useState(null); // { subject, content }
  const [shown, setShown] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => () => timerRef.current && clearInterval(timerRef.current), []);

  // Load orders + types once (cheap; no OpenAI here).
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [oRes, tRes] = await Promise.all([
          authenticatedFetch(`${API_BASE_URL}/orders?shop_domain=${encodeURIComponent(shopDomain)}`),
          authenticatedFetch(`${API_BASE_URL}/templates?shop_domain=${encodeURIComponent(shopDomain)}`),
        ]);
        const oRaw = await oRes.json().catch(() => []);
        const tRaw = await tRes.json().catch(() => []);
        if (!active) return;

        let list = Array.isArray(oRaw) ? oRaw : oRaw?.orders || oRaw?.data || [];
        // Reliable newest-first. Server sort is on `created_at`, which may not exist
        // on the Order model, so we sort client-side with a stable fall-down chain:
        // shopify_created_at → created_at → createdAt → 0, then break ties on _id
        // (Mongo ObjectIds are time-ordered) so the list never jumps between loads.
        const orderTime = (o) => {
          const d = o?.shopify_created_at || o?.created_at || o?.createdAt;
          const t = d ? new Date(d).getTime() : 0;
          return Number.isNaN(t) ? 0 : t;
        };
        list = list
          .slice()
          .sort((a, b) => {
            const diff = orderTime(b) - orderTime(a);
            if (diff !== 0) return diff;
            return String(b?._id || "").localeCompare(String(a?._id || ""));
          })
          .slice(0, 15);
        if (list.length === 0) {
          setIsSample(true);
          setOrders([SAMPLE_ORDER]);
        } else {
          setIsSample(false);
          setOrders(list);
        }
        setOrderIdx(0);

        const tmpls = Array.isArray(tRaw) ? tRaw : tRaw?.templates || tRaw?.data || [];
        const byType = new Map();
        for (const t of tmpls) {
          if (!t?.type || !t?._id) continue;
          const prev = byType.get(t.type);
          if (!prev || (t.is_active && !prev.is_active)) byType.set(t.type, t);
        }
        let typeList = [...byType.values()].map((t) => ({
          type: t.type,
          template_id: t._id,
          label: prettyType(t.type),
        }));
        // No templates yet (brand-new shop) → offer the built-in sample
        // template so the preview still works. Clearly labelled.
        if (typeList.length === 0) {
          typeList = [
            { type: "thank_you", template_id: SAMPLE_TEMPLATE_ID, label: "Thank you (sample template)" },
          ];
        }
        setTypes(typeList);
        setTemplateId((typeList.find((x) => x.type === "thank_you") || typeList[0])?.template_id || "");
        // Let the dashboard know whether REAL templates exist (drives the
        // honest "set up your first template" line in the welcome strip).
        onTemplatesLoaded?.(tmpls.length);
      } catch (e) {
        if (active) {
          setIsSample(true);
          setOrders([SAMPLE_ORDER]);
          // Load failed → sample-template fallback keeps the preview usable.
          setTypes([
            { type: "thank_you", template_id: SAMPLE_TEMPLATE_ID, label: "Thank you (sample template)" },
          ]);
          setTemplateId(SAMPLE_TEMPLATE_ID);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [shopDomain]);

  const run = async () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (!templateId) {
      setPhase("error");
      return;
    }
    setPhase("loading");
    setEmail(null);
    setShown(0);
    const chosen = orderToPreview(orders[orderIdx] || SAMPLE_ORDER);
    // Sample-template mode sends sample_type instead of a template_id — the
    // backend whitelists it and renders through its built-in preview-only
    // template (same real AI, same dryRun/quota-free path).
    const isSampleTemplate = templateId === SAMPLE_TEMPLATE_ID;
    const activeType = types.find((t) => t.template_id === templateId)?.type || "thank_you";
    try {
      const pRes = await authenticatedFetch(
        `${API_BASE_URL}/messages/preview?shop_domain=${encodeURIComponent(shopDomain)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(
            isSampleTemplate
              ? { sample_type: activeType, ...chosen }
              : { template_id: templateId, ...chosen }
          ),
        }
      );
      const pJson = await pRes.json();
      const content = (pJson.content || "").trim();
      const subject = pJson.subject || `Thank you, ${chosen.customer_name}`;
      if (!content) throw new Error("no-content");
      setEmail({ subject, content });

      if (prefersReduced()) {
        setShown(content.length);
        setPhase("done");
        return;
      }
      setPhase("revealing");
      const step = Math.max(1, Math.round(content.length / 240)); // ~4s regardless of length
      let i = 0;
      timerRef.current = setInterval(() => {
        i += step;
        if (i >= content.length) {
          i = content.length;
          clearInterval(timerRef.current);
          setPhase("done");
        }
        setShown(i);
      }, 16);
    } catch (e) {
      setPhase("error");
    }
  };

  const finishNow = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (email) setShown(email.content.length);
    setPhase("done");
  };

  const busy = phase === "loading";
  const activeOrder = orders[orderIdx] || SAMPLE_ORDER;

  return (
    <Card className="overflow-hidden border-indigo-100">
      <div className="bg-gradient-to-br from-indigo-50 via-white to-emerald-50">
        <CardContent className="p-6">
          <div>
            <div className="flex items-center gap-2 text-indigo-700">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100">
                <Mail className="w-4 h-4" />
              </span>
              <span className="text-sm font-medium">Your customer emails</span>
            </div>
            <p className="mt-2 text-sm text-gray-500 max-w-xl leading-relaxed">
              See the email your customer would get — pick a real order and a type, then preview it.
            </p>
          </div>

          {/* Controls: order + type + preview (inline, no modal) */}
          <div className="mt-4 flex flex-col sm:flex-row sm:items-end gap-3">
            <Field label="Order">
              <SelectShell>
                <select
                  value={orderIdx}
                  onChange={(e) => setOrderIdx(Number(e.target.value))}
                  disabled={isSample}
                  className="w-full appearance-none bg-transparent pr-7 text-sm text-gray-800 focus:outline-none disabled:text-gray-500"
                >
                  {orders.map((o, i) => (
                    <option key={i} value={i}>
                      {orderLabel(o)}
                    </option>
                  ))}
                </select>
              </SelectShell>
            </Field>

            <Field label="Email type">
              <SelectShell>
                <select
                  value={templateId}
                  onChange={(e) => setTemplateId(e.target.value)}
                  disabled={types.length === 0}
                  className="w-full appearance-none bg-transparent pr-7 text-sm text-gray-800 focus:outline-none disabled:text-gray-400"
                >
                  {types.length === 0 ? (
                    <option value="">No templates yet</option>
                  ) : (
                    types.map((t) => (
                      <option key={t.template_id} value={t.template_id}>
                        {t.label}
                      </option>
                    ))
                  )}
                </select>
              </SelectShell>
            </Field>

            <button
              onClick={run}
              disabled={busy || !templateId}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
            >
              {phase === "done" ? <RefreshCw className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
              {busy ? "Writing…" : phase === "done" ? "Preview again" : "Preview email"}
            </button>
          </div>

          {/* Email frame */}
          <div className="mt-4 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="px-4 py-2.5 border-b border-gray-100 bg-gray-50/70">
              <div className="flex items-center gap-2 text-[11px] text-gray-400">
                <Mail className="w-3.5 h-3.5" />
                <span>To: {orderToPreview(activeOrder).customer_name}</span>
              </div>
              <p className="text-sm font-semibold text-gray-800 mt-0.5 truncate">
                {phase === "loading" ? (
                  <span className="text-gray-300">Composing subject…</span>
                ) : email && (phase === "revealing" || phase === "done") ? (
                  email.subject
                ) : (
                  <span className="text-gray-300">Subject preview</span>
                )}
              </p>
            </div>

            <div className="px-4 py-4 min-h-[8rem]">
              {phase === "idle" && (
                <p className="text-sm text-gray-400">
                  Pick an order and type, then press <span className="font-medium text-gray-500">Preview email</span> to
                  see exactly what your customer would receive.
                </p>
              )}
              {phase === "loading" && (
                <div className="flex items-center gap-2 text-sm text-indigo-500">
                  <Sparkles className="w-4 h-4 animate-pulse" /> Writing this email…
                </div>
              )}
              {phase === "error" && (
                <p className="text-sm text-gray-500">
                  {types.length === 0
                    ? "No email templates found for this shop yet."
                    : "Couldn’t generate a preview right now. Please try again."}
                </p>
              )}
              {(phase === "revealing" || phase === "done") && email && (
                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {email.content.slice(0, shown)}
                  {phase === "revealing" && (
                    <span className="inline-block w-1.5 h-4 -mb-0.5 bg-indigo-400 animate-pulse" />
                  )}
                </p>
              )}
            </div>
          </div>

          {/* Honest footer */}
          <div className="mt-2 flex items-center justify-between gap-3">
            <p className="text-[11px] text-gray-400 leading-relaxed">
              {isSample
                ? "This is a sample — your real emails will use your actual orders."
                : "A real email our AI writes for this order. Nothing is sent — previews don’t use your quota."}
            </p>
            {phase === "revealing" && (
              <button onClick={finishNow} className="text-[11px] text-indigo-600 hover:text-indigo-700 shrink-0">
                Skip ⏭
              </button>
            )}
          </div>
        </CardContent>
      </div>
    </Card>
  );
}

function Field({ label, children }) {
  return (
    <label className="flex-1 min-w-0">
      <span className="block text-[11px] font-medium text-gray-500 mb-1">{label}</span>
      {children}
    </label>
  );
}

function SelectShell({ children }) {
  return (
    <div className="relative rounded-lg border border-gray-200 bg-white px-3 py-2">
      {children}
      <ChevronDown className="w-4 h-4 text-gray-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
    </div>
  );
}
